<template>
  <el-dialog
    :model-value="visible"
    :title="title"
    width="700px"
    destroy-on-close
    @update:model-value="$emit('update:visible', $event)"
  >
    <div v-if="resolvedLoading" class="d-flex justify-content-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <template v-else>
      <table class="table table-sm uld-table">
        <thead>
          <tr>
            <th>{{ $t('UserListDialog.userId') }}</th>
            <th>{{ $t('UserListDialog.name') }}</th>
            <slot name="extra-th" />
          </tr>
        </thead>
        <tbody>
          <tr v-if="!resolvedUsers.length">
            <td :colspan="hasExtraColumn ? 3 : 2" class="text-center text-muted py-3">
              {{ $t('General.NoData') }}
            </td>
          </tr>
          <tr v-for="user in resolvedUsers" :key="user.user_id">
            <td class="uld-id">{{ user.user_id }}</td>
            <td class="uld-name-cell">
              <div v-if="user.first_name || user.last_name" class="uld-fullname">
                {{ [user.first_name, user.last_name].filter(Boolean).join(' ') }}
              </div>
              <div v-if="user.username" class="uld-username">@{{ user.username }}</div>
              <span v-if="!user.first_name && !user.last_name && !user.username" class="text-muted">—</span>
            </td>
            <slot name="extra-td" :row="user" />
          </tr>
        </tbody>
      </table>

      <div class="uld-footer">
        <span class="text-muted uld-total">
          {{ $t('UserListDialog.total') }}: <strong>{{ total }}</strong>
        </span>
        <el-pagination
          v-if="total > pageSize"
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="prev, pager, next"
          @current-change="$emit('update:page', $event)"
        />
      </div>
    </template>
  </el-dialog>
</template>

<script>
import CohortsService from '@/helpers/api/cohorts.service'

export default {
  name: 'UserListDialog',
  emits: ['update:visible', 'update:page'],

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    users: {
      type: Array,
      default: () => [],
    },
    userIds: {
      type: Array,
      default: null,
    },
    resourceKey: {
      type: String,
      default: null,
    },
    total: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 50,
    },
  },

  data() {
    return {
      internalUsers: [],
      internalLoading: false,
    }
  },

  computed: {
    hasExtraColumn() {
      return !!this.$slots['extra-th']
    },
    resolvedUsers() {
      return this.userIds !== null ? this.internalUsers : this.users
    },
    resolvedLoading() {
      return this.userIds !== null ? (this.internalLoading || this.loading) : this.loading
    },
  },

  watch: {
    userIds(ids) {
      if (this.visible && ids !== null) {
        this.fetchProfiles(ids)
      }
    },
    visible(val) {
      if (val && this.userIds !== null) {
        this.fetchProfiles(this.userIds)
      }
    },
  },

  methods: {
    async fetchProfiles(ids) {
      if (!ids || ids.length === 0) {
        this.internalUsers = []
        return
      }
      this.internalLoading = true
      try {
        const result = await CohortsService.userProfiles(ids, this.resourceKey)
        this.internalUsers = result?.users ?? []
      } catch (e) {
        console.error('UserListDialog: failed to load profiles', e)
        this.internalUsers = []
      } finally {
        this.internalLoading = false
      }
    },
  },
}
</script>

<style scoped>
.uld-table {
  width: 100%;
  margin-bottom: 0;
}

.uld-table th {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  background: #fafafa;
}

.uld-id {
  font-size: 12px;
  white-space: nowrap;
  color: #909399;
}

.uld-name-cell {
  line-height: 1.3;
}

.uld-fullname {
  font-size: 13px;
  color: #303133;
  font-weight: 500;
}

.uld-username {
  font-size: 12px;
  color: #909399;
}

.uld-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.uld-total {
  font-size: 13px;
}
</style>
